import React, { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { FileText, LogOut, Save, RotateCcw, ChevronLeft, Copy, Check, Edit3, Eye, Upload, Download, FileUp, Shield, ShieldOff, Plus, Trash2, X, FolderOpen, Pencil } from 'lucide-react';
import { toast } from 'sonner';

const API_URL = process.env.REACT_APP_BACKEND_URL;

// Template categories
var CATEGORIES = {
    wills: 'Wills',
    trusts: 'Trusts',
    poa: 'Powers of Attorney',
    amd: 'Advance Medical Directive',
    letters: 'Letters',
    other: 'Other Documents'
};

function TemplateManagement() {
    const auth = useAuth();
    const [templates, setTemplates] = useState([]);
    const [selectedTemplate, setSelectedTemplate] = useState(null);
    const [templateContent, setTemplateContent] = useState('');
    const [originalContent, setOriginalContent] = useState('');
    const [placeholders, setPlaceholders] = useState([]);
    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [editMode, setEditMode] = useState(false);
    const [copiedPlaceholder, setCopiedPlaceholder] = useState(null);
    const [showAddModal, setShowAddModal] = useState(false);
    const [newTemplateName, setNewTemplateName] = useState('');
    const [newTemplateCategory, setNewTemplateCategory] = useState('other');
    const [newTemplateContent, setNewTemplateContent] = useState('');
    const [newTemplateFile, setNewTemplateFile] = useState(null);
    const [createMode, setCreateMode] = useState('text'); // 'text' or 'upload'
    const [categoryFilter, setCategoryFilter] = useState('all');
    const [showRenameModal, setShowRenameModal] = useState(false);
    const [renameValue, setRenameValue] = useState('');
    const fileInputRef = useRef(null);
    const newFileInputRef = useRef(null);

    const el = React.createElement;

    useEffect(function() {
        loadTemplates();
    }, []);

    function loadTemplates() {
        setLoading(true);
        axios.get(API_URL + '/api/templates').then(function(res) {
            setTemplates(res.data);
            if (res.data.length > 0 && !selectedTemplate) {
                loadTemplate(res.data[0].name);
            }
        }).catch(function() {
            toast.error('Failed to load templates');
        }).finally(function() {
            setLoading(false);
        });
    }

    function loadTemplate(name) {
        setLoading(true);
        axios.get(API_URL + '/api/templates/' + name).then(function(res) {
            // Get info from templates list (display_name, protected status, is_builtin)
            var templateInfo = templates.find(function(t) { return t.name === name; });
            setSelectedTemplate({
                ...res.data,
                // Use display_name from templates list if available (accounts for renamed templates)
                display_name: templateInfo?.display_name || res.data.display_name,
                protected: templateInfo?.protected || false,
                is_builtin: templateInfo?.is_builtin !== false
            });
            setTemplateContent(res.data.content);
            setOriginalContent(res.data.content);
            setPlaceholders(res.data.placeholders || []);
            setEditMode(false);
        }).catch(function() {
            toast.error('Failed to load template');
        }).finally(function() {
            setLoading(false);
        });
    }

    function saveTemplate() {
        if (!selectedTemplate) return;
        setSaving(true);
        axios.put(API_URL + '/api/templates/' + selectedTemplate.name, {
            content: templateContent
        }).then(function() {
            toast.success('Template saved successfully');
            setOriginalContent(templateContent);
            setEditMode(false);
            loadTemplates();
        }).catch(function() {
            toast.error('Failed to save template');
        }).finally(function() {
            setSaving(false);
        });
    }

    function resetTemplate() {
        if (!selectedTemplate) return;
        if (!window.confirm('Are you sure you want to reset this template to default? This will remove any uploaded Word document. This cannot be undone.')) return;
        
        setSaving(true);
        axios.delete(API_URL + '/api/templates/' + selectedTemplate.name).then(function() {
            toast.success('Template reset to default');
            loadTemplate(selectedTemplate.name);
            loadTemplates();
        }).catch(function() {
            toast.error('Failed to reset template');
        }).finally(function() {
            setSaving(false);
        });
    }

    function handleUploadClick() {
        if (fileInputRef.current) {
            fileInputRef.current.click();
        }
    }

    function handleFileUpload(event) {
        var file = event.target.files[0];
        if (!file) return;
        
        if (!file.name.endsWith('.docx')) {
            toast.error('Please upload a .docx file. If you have a .doc file, please save it as .docx first.');
            return;
        }
        
        if (!selectedTemplate) {
            toast.error('Please select a template first');
            return;
        }
        
        setUploading(true);
        var formData = new FormData();
        formData.append('file', file);
        
        axios.post(API_URL + '/api/templates/' + selectedTemplate.name + '/upload', formData, {
            headers: { 'Content-Type': 'multipart/form-data' }
        }).then(function(res) {
            toast.success('Template uploaded successfully! Found ' + res.data.placeholder_count + ' placeholders.');
            loadTemplate(selectedTemplate.name);
            loadTemplates();
        }).catch(function(err) {
            var message = err.response?.data?.detail || 'Failed to upload template';
            toast.error(message);
        }).finally(function() {
            setUploading(false);
            // Reset file input
            if (fileInputRef.current) {
                fileInputRef.current.value = '';
            }
        });
    }

    function downloadTemplate() {
        if (!selectedTemplate) return;
        axios.get(API_URL + '/api/templates/' + selectedTemplate.name + '/download', {
            responseType: 'blob'
        }).then(function(res) {
            // Create download link
            var url = window.URL.createObjectURL(new Blob([res.data]));
            var link = document.createElement('a');
            link.href = url;
            link.setAttribute('download', selectedTemplate.name + '_template.docx');
            document.body.appendChild(link);
            link.click();
            link.remove();
            window.URL.revokeObjectURL(url);
        }).catch(function(err) {
            toast.error('Failed to download template');
        });
    }

    function toggleProtection() {
        if (!selectedTemplate) return;
        axios.patch(API_URL + '/api/templates/' + selectedTemplate.name + '/protect').then(function(res) {
            toast.success(res.data.message);
            loadTemplates();
            // Update selected template protection status
            setSelectedTemplate(function(prev) {
                return prev ? { ...prev, protected: res.data.protected } : prev;
            });
        }).catch(function(err) {
            var message = err.response?.data?.detail || 'Failed to update protection';
            toast.error(message);
        });
    }

    function copyPlaceholder(placeholder) {
        navigator.clipboard.writeText('{{' + placeholder + '}}');
        setCopiedPlaceholder(placeholder);
        toast.success('Placeholder copied!');
        setTimeout(function() {
            setCopiedPlaceholder(null);
        }, 2000);
    }

    function cancelEdit() {
        setTemplateContent(originalContent);
        setEditMode(false);
    }

    function copyAllPlaceholders() {
        var allPlaceholders = placeholders.map(function(p) { return '{{' + p + '}}'; }).join('\n');
        navigator.clipboard.writeText(allPlaceholders);
        toast.success('All ' + placeholders.length + ' placeholders copied!');
    }

    function openAddModal() {
        setNewTemplateName('');
        setNewTemplateCategory('other');
        setNewTemplateContent('');
        setNewTemplateFile(null);
        setCreateMode('text');
        setShowAddModal(true);
    }

    function closeAddModal() {
        setShowAddModal(false);
        setNewTemplateName('');
        setNewTemplateCategory('other');
        setNewTemplateContent('');
        setNewTemplateFile(null);
    }

    function handleNewFileSelect(event) {
        var file = event.target.files[0];
        if (file && file.name.endsWith('.docx')) {
            setNewTemplateFile(file);
        } else if (file) {
            toast.error('Please select a .docx file');
        }
    }

    function createTemplate() {
        if (!newTemplateName.trim()) {
            toast.error('Please enter a template name');
            return;
        }
        
        setSaving(true);
        
        // First create the template record
        axios.post(API_URL + '/api/templates', {
            name: newTemplateName.trim(),
            category: newTemplateCategory,
            content: createMode === 'text' ? newTemplateContent : null
        }).then(function(res) {
            var templateSlug = res.data.name;
            
            // If upload mode and file selected, upload the file
            if (createMode === 'upload' && newTemplateFile) {
                var formData = new FormData();
                formData.append('file', newTemplateFile);
                
                return axios.post(API_URL + '/api/templates/' + templateSlug + '/upload-new', formData, {
                    headers: { 'Content-Type': 'multipart/form-data' }
                }).then(function(uploadRes) {
                    toast.success('Template "' + newTemplateName + '" created with ' + uploadRes.data.placeholder_count + ' placeholders');
                    return templateSlug;
                });
            } else {
                toast.success('Template "' + newTemplateName + '" created successfully');
                return templateSlug;
            }
        }).then(function(templateSlug) {
            closeAddModal();
            loadTemplates();
            // Select the newly created template
            if (templateSlug) {
                setTimeout(function() {
                    loadTemplate(templateSlug);
                }, 500);
            }
        }).catch(function(err) {
            var message = err.response?.data?.detail || 'Failed to create template';
            toast.error(message);
        }).finally(function() {
            setSaving(false);
        });
    }

    function deleteTemplate() {
        if (!selectedTemplate) return;
        
        var isBuiltin = selectedTemplate.is_builtin !== false;
        var confirmMsg = isBuiltin 
            ? 'Are you sure you want to reset "' + selectedTemplate.display_name + '" to default?'
            : 'Are you sure you want to permanently delete "' + selectedTemplate.display_name + '"? This cannot be undone.';
        
        if (!window.confirm(confirmMsg)) return;
        
        axios.delete(API_URL + '/api/templates/' + selectedTemplate.name).then(function(res) {
            toast.success(res.data.message);
            setSelectedTemplate(null);
            loadTemplates();
        }).catch(function(err) {
            var message = err.response?.data?.detail || 'Failed to delete template';
            toast.error(message);
        });
    }

    function openRenameModal() {
        if (!selectedTemplate) return;
        setRenameValue(selectedTemplate.display_name);
        setShowRenameModal(true);
    }

    function closeRenameModal() {
        setShowRenameModal(false);
        setRenameValue('');
    }

    function renameTemplate() {
        if (!selectedTemplate || !renameValue.trim()) return;
        
        setSaving(true);
        axios.patch(API_URL + '/api/templates/' + selectedTemplate.name + '/rename', {
            display_name: renameValue.trim()
        }).then(function(res) {
            toast.success(res.data.message);
            closeRenameModal();
            // Update the selected template with new display name
            setSelectedTemplate(function(prev) {
                return prev ? { ...prev, display_name: renameValue.trim() } : prev;
            });
            loadTemplates();
        }).catch(function(err) {
            var message = err.response?.data?.detail || 'Failed to rename template';
            toast.error(message);
        }).finally(function() {
            setSaving(false);
        });
    }

    // Filter templates by category
    var filteredTemplates = categoryFilter === 'all' 
        ? templates 
        : templates.filter(function(t) { return t.category === categoryFilter; });

    // Group templates by category for display
    var templatesByCategory = {};
    filteredTemplates.forEach(function(t) {
        var cat = t.category || 'other';
        if (!templatesByCategory[cat]) {
            templatesByCategory[cat] = [];
        }
        templatesByCategory[cat].push(t);
    });

    var hasChanges = templateContent !== originalContent;
    var isDocx = selectedTemplate?.is_docx;
    var isAdmin = auth.user?.role === 'admin';
    var isProtected = selectedTemplate?.protected;
    var isBuiltin = selectedTemplate?.is_builtin !== false;

    // Header
    var header = el('header', { className: 'sticky top-0 z-50 border-b border-stone-200 bg-white/80 backdrop-blur-sm' },
        el('div', { className: 'flex items-center justify-between px-6 py-4 max-w-7xl mx-auto' },
            el(Link, { to: '/dashboard', className: 'flex items-center gap-3' },
                el('img', { src: '/lion-logo.png', alt: 'BestWillsAndTrusts', className: 'h-10 w-10 object-contain' }),
                el('span', { className: 'font-serif text-xl font-semibold hidden sm:block text-amber-600' }, 'BestWillsAndTrusts')
            ),
            el('nav', { className: 'hidden md:flex items-center gap-8' },
                el(Link, { to: '/dashboard', className: 'text-sm font-medium text-muted-foreground hover:text-foreground' }, 'Dashboard'),
                el(Link, { to: '/clients', className: 'text-sm font-medium text-muted-foreground hover:text-foreground' }, 'Clients'),
                el(Link, { to: '/documents', className: 'text-sm font-medium text-muted-foreground hover:text-foreground' }, 'Documents'),
                el(Link, { to: '/templates', className: 'text-sm font-medium text-amber-600' }, 'Templates'),
                auth.user?.role === 'admin' && el(Link, { to: '/admin/users', className: 'text-sm font-medium text-muted-foreground hover:text-foreground' }, 'Users')
            ),
            el('div', { className: 'flex items-center gap-4' },
                el('span', { className: 'text-sm text-muted-foreground hidden sm:block' }, auth.user?.name),
                el(Button, { variant: 'ghost', size: 'sm', onClick: function() { auth.logout(); }, className: 'rounded-none cursor-pointer', 'data-testid': 'logout-btn' },
                    el(LogOut, { className: 'h-4 w-4 mr-2' }), 'Sign Out'
                )
            )
        )
    );

    // Page title and back button
    var pageTitle = el('div', { className: 'mb-6' },
        el(Link, { to: '/dashboard', className: 'inline-flex items-center text-sm text-muted-foreground hover:text-foreground mb-4' },
            el(ChevronLeft, { className: 'h-4 w-4 mr-1' }), 'Back to Dashboard'
        ),
        el('h1', { className: 'font-serif text-3xl lg:text-4xl mb-2', 'data-testid': 'page-title' }, 'Template Management'),
        el('p', { className: 'text-muted-foreground' }, 'Upload Word documents or edit text templates. Use {{placeholder}} syntax for dynamic fields.')
    );

    // Category filter buttons
    var categoryFilterBar = el('div', { className: 'flex flex-wrap gap-2 mb-3 px-4' },
        el('button', {
            onClick: function() { setCategoryFilter('all'); },
            className: 'text-xs px-2 py-1 rounded-sm transition-colors ' + (categoryFilter === 'all' ? 'bg-amber-100 text-amber-700 font-medium' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'),
            'data-testid': 'filter-all'
        }, 'All'),
        Object.keys(CATEGORIES).map(function(cat) {
            return el('button', {
                key: cat,
                onClick: function() { setCategoryFilter(cat); },
                className: 'text-xs px-2 py-1 rounded-sm transition-colors ' + (categoryFilter === cat ? 'bg-amber-100 text-amber-700 font-medium' : 'bg-stone-100 text-stone-600 hover:bg-stone-200'),
                'data-testid': 'filter-' + cat
            }, CATEGORIES[cat]);
        })
    );

    // Template list sidebar
    var templateList = el(Card, { className: 'rounded-none border-stone-200' },
        el(CardHeader, { className: 'pb-3' },
            el('div', { className: 'flex items-center justify-between' },
                el('div', null,
                    el(CardTitle, { className: 'font-serif text-lg' }, 'Templates'),
                    el(CardDescription, null, 'Select a template to edit')
                ),
                isAdmin && el(Button, {
                    size: 'sm',
                    onClick: openAddModal,
                    className: 'rounded-none bg-amber-600 hover:bg-amber-700',
                    'data-testid': 'add-template-btn'
                },
                    el(Plus, { className: 'h-4 w-4 mr-1' }), 'Add'
                )
            )
        ),
        el(CardContent, { className: 'p-0' },
            categoryFilterBar,
            el('div', { className: 'divide-y divide-stone-200' },
                Object.keys(templatesByCategory).map(function(category) {
                    return el('div', { key: category },
                        el('div', { className: 'px-4 py-2 bg-stone-50 border-b border-stone-200' },
                            el('div', { className: 'flex items-center gap-2' },
                                el(FolderOpen, { className: 'h-3 w-3 text-stone-500' }),
                                el('span', { className: 'text-xs font-medium text-stone-600 uppercase tracking-wide' }, CATEGORIES[category] || category)
                            )
                        ),
                        templatesByCategory[category].map(function(t) {
                            var isSelected = selectedTemplate?.name === t.name;
                            var tIsBuiltin = t.is_builtin !== false;
                            return el('button', {
                                key: t.name,
                                onClick: function() { loadTemplate(t.name); },
                                className: 'w-full text-left px-4 py-3 hover:bg-stone-50 flex flex-col gap-1 ' + (isSelected ? 'bg-amber-50 border-l-2 border-amber-500' : ''),
                                'data-testid': 'template-item-' + t.name
                            },
                                el('div', { className: 'flex items-center justify-between w-full' },
                                    el('div', { className: 'flex items-center gap-2' },
                                        el(Shield, { className: 'h-4 w-4 ' + (t.protected ? 'text-red-600' : 'text-green-600') }),
                                        el('p', { className: 'font-medium text-sm' + (isSelected ? ' text-amber-700' : '') }, t.display_name)
                                    ),
                                    el('div', { className: 'flex items-center gap-1' },
                                        el('span', { className: 'text-xs px-2 py-0.5 ' + (t.protected ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700') }, t.protected ? 'Locked' : 'Unlocked'),
                                        t.is_docx && el('span', { className: 'text-xs bg-blue-100 text-blue-700 px-2 py-0.5' }, 'Word'),
                                        !tIsBuiltin && el('span', { className: 'text-xs bg-purple-100 text-purple-700 px-2 py-0.5' }, 'Custom')
                                    )
                                ),
                                // File name and upload date row - show original filename if available
                                (t.original_filename || t.file_name || t.upload_date) && el('div', { className: 'flex items-center gap-2 text-xs text-muted-foreground pl-6' },
                                    el('span', null, t.original_filename || t.file_name || 'No file'),
                                    t.upload_date && el('span', null, '• ' + t.upload_date)
                                )
                            );
                        })
                    );
                })
            ),
            filteredTemplates.length === 0 && el('div', { className: 'px-4 py-8 text-center text-sm text-muted-foreground' }, 'No templates found')
        )
    );

    // Placeholders panel
    var placeholdersPanel = el(Card, { className: 'rounded-none border-stone-200 mt-4' },
        el(CardHeader, { className: 'pb-3' },
            el(CardTitle, { className: 'font-serif text-lg' }, 'Placeholders'),
            el(CardDescription, null, 
                placeholders.length > 0 
                    ? el('span', { className: 'flex items-center gap-2' },
                        'Click to copy individual or ',
                        el('button', {
                            onClick: copyAllPlaceholders,
                            className: 'text-amber-600 hover:text-amber-700 underline font-medium',
                            'data-testid': 'copy-all-placeholders-btn'
                        }, 'copy all')
                    )
                    : 'Upload a template to see placeholders'
            )
        ),
        el(CardContent, { className: 'max-h-64 overflow-y-auto' },
            placeholders.length > 0 
                ? el('div', { className: 'flex flex-wrap gap-2' },
                    placeholders.map(function(p) {
                        var isCopied = copiedPlaceholder === p;
                        return el('button', {
                            key: p,
                            onClick: function() { copyPlaceholder(p); },
                            className: 'inline-flex items-center gap-1 text-xs px-2 py-1 bg-stone-100 hover:bg-stone-200 text-stone-700 font-mono transition-colors ' + (isCopied ? 'bg-green-100 text-green-700' : ''),
                            'data-testid': 'placeholder-' + p
                        },
                            isCopied ? el(Check, { className: 'h-3 w-3' }) : el(Copy, { className: 'h-3 w-3' }),
                            '{{' + p + '}}'
                        );
                    })
                )
                : el('p', { className: 'text-sm text-muted-foreground' }, 'No placeholders found')
        )
    );

    // Hidden file input
    var fileInput = el('input', {
        ref: fileInputRef,
        type: 'file',
        accept: '.docx',
        onChange: handleFileUpload,
        style: { display: 'none' },
        'data-testid': 'file-input'
    });

    // Editor header with upload button
    var editorHeader = el('div', { className: 'flex flex-col sm:flex-row sm:items-center justify-between gap-4 mb-4' },
        el('div', null,
            el('div', { className: 'flex items-center gap-2' },
                el('h2', { className: 'font-serif text-xl' }, selectedTemplate?.display_name || 'Select a template'),
                isDocx && el('span', { className: 'text-xs bg-blue-100 text-blue-700 px-2 py-1 font-medium' }, 'Word Document'),
                selectedTemplate && el('span', { className: 'text-xs px-2 py-1 font-medium flex items-center gap-1 ' + (isProtected ? 'bg-red-100 text-red-700' : 'bg-green-100 text-green-700') }, 
                    el(Shield, { className: 'h-3 w-3' }), isProtected ? 'Locked' : 'Unlocked'
                )
            ),
            selectedTemplate && el('p', { className: 'text-sm text-muted-foreground mt-1' }, 
                'File: /backend/templates/' + selectedTemplate.name + (isDocx ? '.docx' : '.txt')
            )
        ),
        selectedTemplate && el('div', { className: 'flex flex-wrap items-center gap-2' },
            // Rename button (admin only)
            isAdmin && el(Button, {
                variant: 'outline',
                size: 'sm',
                onClick: openRenameModal,
                className: 'rounded-none',
                'data-testid': 'rename-template-btn'
            },
                el(Pencil, { className: 'h-4 w-4 mr-2' }), 'Rename'
            ),
            // Protection toggle button (admin only)
            isAdmin && el(Button, {
                variant: isProtected ? 'default' : 'outline',
                size: 'sm',
                onClick: toggleProtection,
                className: 'rounded-none ' + (isProtected ? 'bg-green-600 hover:bg-green-700' : ''),
                'data-testid': 'protect-template-btn'
            },
                isProtected ? el(ShieldOff, { className: 'h-4 w-4 mr-2' }) : el(Shield, { className: 'h-4 w-4 mr-2' }),
                isProtected ? 'Unlock' : 'Lock'
            ),
            // Upload button (admin only, disabled if protected)
            isAdmin && el(Button, {
                variant: 'outline',
                size: 'sm',
                onClick: handleUploadClick,
                disabled: uploading || isProtected,
                className: 'rounded-none',
                title: isProtected ? 'Cannot upload to locked template' : '',
                'data-testid': 'upload-template-btn'
            },
                el(Upload, { className: 'h-4 w-4 mr-2' }),
                uploading ? 'Uploading...' : 'Upload Word Doc'
            ),
            // Download button (admin only, if template has docx file)
            isAdmin && selectedTemplate.is_docx && el(Button, {
                variant: 'outline',
                size: 'sm',
                onClick: downloadTemplate,
                className: 'rounded-none',
                'data-testid': 'download-template-btn'
            },
                el(Download, { className: 'h-4 w-4 mr-2' }), 'Download Template'
            ),
            // Edit/Save buttons (only for text templates, disabled if protected)
            !isDocx && (editMode ? [
                el(Button, {
                    key: 'cancel',
                    variant: 'outline',
                    size: 'sm',
                    onClick: cancelEdit,
                    className: 'rounded-none',
                    'data-testid': 'cancel-edit-btn'
                }, 'Cancel'),
                el(Button, {
                    key: 'save',
                    size: 'sm',
                    onClick: saveTemplate,
                    disabled: saving || !hasChanges || isProtected,
                    className: 'rounded-none',
                    'data-testid': 'save-template-btn'
                },
                    el(Save, { className: 'h-4 w-4 mr-2' }),
                    saving ? 'Saving...' : 'Save Changes'
                )
            ] : el(Button, {
                variant: 'outline',
                size: 'sm',
                onClick: function() { setEditMode(true); },
                disabled: isProtected,
                className: 'rounded-none',
                title: isProtected ? 'Cannot edit locked template' : '',
                'data-testid': 'edit-template-btn'
            },
                el(Edit3, { className: 'h-4 w-4 mr-2' }), 'Edit Text'
            )),
            // Reset/Delete button (admin only, disabled if protected)
            isAdmin && el(Button, {
                variant: 'ghost',
                size: 'sm',
                onClick: deleteTemplate,
                disabled: isProtected,
                className: 'rounded-none text-red-600 hover:text-red-700 hover:bg-red-50',
                title: isProtected ? 'Cannot ' + (isBuiltin ? 'reset' : 'delete') + ' locked template' : '',
                'data-testid': 'delete-template-btn'
            },
                isBuiltin ? el(RotateCcw, { className: 'h-4 w-4 mr-2' }) : el(Trash2, { className: 'h-4 w-4 mr-2' }),
                isBuiltin ? 'Reset' : 'Delete'
            )
        )
    );

    // Info banner for Word documents
    var docxInfoBanner = isDocx && el('div', { className: 'mb-4 p-4 bg-blue-50 border border-blue-200' },
        el('div', { className: 'flex items-start gap-3' },
            el(FileText, { className: 'h-5 w-5 text-blue-600 mt-0.5' }),
            el('div', null,
                el('p', { className: 'font-medium text-blue-900' }, 'Word Document Template'),
                el('p', { className: 'text-sm text-blue-700 mt-1' }, 
                    'This template preserves your Word formatting (fonts, styles, headers, etc.). ' +
                    'The preview below shows text content only. Generated documents will include full formatting.'
                ),
                el('p', { className: 'text-sm text-blue-700 mt-1' }, 
                    'To edit, download the template, modify it in Word, and re-upload.'
                )
            )
        )
    );

    var editorContent = loading 
        ? el('div', { className: 'h-96 bg-stone-100 animate-pulse' })
        : editMode && !isDocx
            ? el('textarea', {
                value: templateContent,
                onChange: function(e) { setTemplateContent(e.target.value); },
                className: 'w-full h-[500px] p-4 font-mono text-sm border border-stone-300 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none resize-none bg-white',
                placeholder: 'Template content...',
                'data-testid': 'template-editor'
            })
            : el('div', { className: 'relative' },
                el('div', { className: 'absolute top-2 right-2 flex items-center gap-1 text-xs text-muted-foreground bg-white px-2 py-1' },
                    el(Eye, { className: 'h-3 w-3' }), isDocx ? 'Text Preview' : 'Preview Mode'
                ),
                el('pre', { 
                    className: 'w-full h-[500px] p-4 font-mono text-sm border border-stone-200 overflow-auto whitespace-pre-wrap ' + (isDocx ? 'bg-blue-50/50' : 'bg-stone-50'), 
                    'data-testid': 'template-preview' 
                },
                    templateContent || 'No content'
                )
            );

    var editorPanel = el(Card, { className: 'rounded-none border-stone-200' },
        el(CardContent, { className: 'p-6' },
            fileInput,
            editorHeader,
            docxInfoBanner,
            editorContent
        )
    );

    // Main layout
    var mainContent = el('main', { className: 'max-w-7xl mx-auto px-6 py-8' },
        pageTitle,
        el('div', { className: 'grid grid-cols-1 lg:grid-cols-4 gap-6' },
            el('div', { className: 'lg:col-span-1' },
                templateList,
                placeholdersPanel
            ),
            el('div', { className: 'lg:col-span-3' },
                editorPanel
            )
        )
    );

    // Hidden file input for new template upload
    var newFileInput = el('input', {
        ref: newFileInputRef,
        type: 'file',
        accept: '.docx',
        onChange: handleNewFileSelect,
        style: { display: 'none' },
        'data-testid': 'new-file-input'
    });

    // Add Template Modal
    var addTemplateModal = showAddModal && el('div', { className: 'fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50', 'data-testid': 'add-template-modal' },
        el('div', { className: 'bg-white w-full max-w-lg max-h-[90vh] overflow-y-auto' },
            // Modal header
            el('div', { className: 'flex items-center justify-between p-4 border-b border-stone-200' },
                el('h3', { className: 'font-serif text-xl' }, 'Add New Template'),
                el('button', {
                    onClick: closeAddModal,
                    className: 'p-1 hover:bg-stone-100 rounded-sm',
                    'data-testid': 'close-modal-btn'
                }, el(X, { className: 'h-5 w-5' }))
            ),
            // Modal content
            el('div', { className: 'p-4 space-y-4' },
                // Template name
                el('div', null,
                    el('label', { className: 'block text-sm font-medium mb-1' }, 'Template Name *'),
                    el('input', {
                        type: 'text',
                        value: newTemplateName,
                        onChange: function(e) { setNewTemplateName(e.target.value); },
                        placeholder: 'e.g., Healthcare Directive, Pet Trust',
                        className: 'w-full px-3 py-2 border border-stone-300 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none text-sm',
                        'data-testid': 'new-template-name'
                    })
                ),
                // Category
                el('div', null,
                    el('label', { className: 'block text-sm font-medium mb-1' }, 'Category *'),
                    el('select', {
                        value: newTemplateCategory,
                        onChange: function(e) { setNewTemplateCategory(e.target.value); },
                        className: 'w-full px-3 py-2 border border-stone-300 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none text-sm bg-white',
                        'data-testid': 'new-template-category'
                    },
                        Object.keys(CATEGORIES).map(function(cat) {
                            return el('option', { key: cat, value: cat }, CATEGORIES[cat]);
                        })
                    )
                ),
                // Creation mode toggle
                el('div', null,
                    el('label', { className: 'block text-sm font-medium mb-2' }, 'Template Content'),
                    el('div', { className: 'flex gap-2' },
                        el('button', {
                            onClick: function() { setCreateMode('text'); setNewTemplateFile(null); },
                            className: 'flex-1 px-3 py-2 text-sm border transition-colors ' + (createMode === 'text' ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-stone-300 hover:bg-stone-50'),
                            'data-testid': 'mode-text-btn'
                        },
                            el(Edit3, { className: 'h-4 w-4 inline mr-2' }), 'Write Text'
                        ),
                        el('button', {
                            onClick: function() { setCreateMode('upload'); setNewTemplateContent(''); },
                            className: 'flex-1 px-3 py-2 text-sm border transition-colors ' + (createMode === 'upload' ? 'border-amber-500 bg-amber-50 text-amber-700' : 'border-stone-300 hover:bg-stone-50'),
                            'data-testid': 'mode-upload-btn'
                        },
                            el(Upload, { className: 'h-4 w-4 inline mr-2' }), 'Upload .docx'
                        )
                    )
                ),
                // Text content area (if text mode)
                createMode === 'text' && el('div', null,
                    el('textarea', {
                        value: newTemplateContent,
                        onChange: function(e) { setNewTemplateContent(e.target.value); },
                        placeholder: 'Enter template content here. Use {{placeholder_name}} for dynamic fields.\n\nExample:\nDear {{client_full_name}},\n\nThis letter confirms...',
                        className: 'w-full h-48 px-3 py-2 border border-stone-300 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none text-sm font-mono resize-none',
                        'data-testid': 'new-template-content'
                    }),
                    el('p', { className: 'text-xs text-muted-foreground mt-1' }, 'Tip: Use placeholders like {{client_full_name}}, {{date_of_birth}}, {{address}} etc.')
                ),
                // File upload area (if upload mode)
                createMode === 'upload' && el('div', { className: 'border-2 border-dashed border-stone-300 p-6 text-center' },
                    newTemplateFile 
                        ? el('div', null,
                            el(FileText, { className: 'h-8 w-8 mx-auto text-blue-600 mb-2' }),
                            el('p', { className: 'text-sm font-medium' }, newTemplateFile.name),
                            el('button', {
                                onClick: function() { setNewTemplateFile(null); },
                                className: 'text-xs text-red-600 hover:underline mt-1'
                            }, 'Remove')
                        )
                        : el('div', null,
                            el(Upload, { className: 'h-8 w-8 mx-auto text-stone-400 mb-2' }),
                            el('p', { className: 'text-sm text-muted-foreground mb-2' }, 'Select a Word document (.docx)'),
                            el(Button, {
                                variant: 'outline',
                                size: 'sm',
                                onClick: function() { newFileInputRef.current?.click(); },
                                className: 'rounded-none',
                                'data-testid': 'select-file-btn'
                            }, 'Choose File')
                        ),
                    newFileInput
                )
            ),
            // Modal footer
            el('div', { className: 'flex justify-end gap-2 p-4 border-t border-stone-200' },
                el(Button, {
                    variant: 'outline',
                    onClick: closeAddModal,
                    className: 'rounded-none',
                    'data-testid': 'cancel-add-btn'
                }, 'Cancel'),
                el(Button, {
                    onClick: createTemplate,
                    disabled: saving || !newTemplateName.trim() || (createMode === 'upload' && !newTemplateFile),
                    className: 'rounded-none bg-amber-600 hover:bg-amber-700',
                    'data-testid': 'create-template-btn'
                },
                    saving ? 'Creating...' : 'Create Template'
                )
            )
        )
    );

    // Rename Template Modal
    var renameTemplateModal = showRenameModal && el('div', { className: 'fixed inset-0 z-50 flex items-center justify-center p-4 bg-black/50', 'data-testid': 'rename-template-modal' },
        el('div', { className: 'bg-white w-full max-w-md' },
            // Modal header
            el('div', { className: 'flex items-center justify-between p-4 border-b border-stone-200' },
                el('h3', { className: 'font-serif text-xl' }, 'Rename Template'),
                el('button', {
                    onClick: closeRenameModal,
                    className: 'p-1 hover:bg-stone-100 rounded-sm',
                    'data-testid': 'close-rename-modal-btn'
                }, el(X, { className: 'h-5 w-5' }))
            ),
            // Modal content
            el('div', { className: 'p-4' },
                el('label', { className: 'block text-sm font-medium mb-1' }, 'Display Name'),
                el('input', {
                    type: 'text',
                    value: renameValue,
                    onChange: function(e) { setRenameValue(e.target.value); },
                    placeholder: 'Enter new display name',
                    className: 'w-full px-3 py-2 border border-stone-300 focus:border-amber-500 focus:ring-1 focus:ring-amber-500 outline-none text-sm',
                    'data-testid': 'rename-input',
                    onKeyDown: function(e) {
                        if (e.key === 'Enter' && renameValue.trim()) {
                            renameTemplate();
                        }
                    }
                }),
                el('p', { className: 'text-xs text-muted-foreground mt-2' }, 
                    'This only changes how the template appears in the list. The file name (' + (selectedTemplate?.name || '') + ') will remain the same.'
                )
            ),
            // Modal footer
            el('div', { className: 'flex justify-end gap-2 p-4 border-t border-stone-200' },
                el(Button, {
                    variant: 'outline',
                    onClick: closeRenameModal,
                    className: 'rounded-none',
                    'data-testid': 'cancel-rename-btn'
                }, 'Cancel'),
                el(Button, {
                    onClick: renameTemplate,
                    disabled: saving || !renameValue.trim(),
                    className: 'rounded-none bg-amber-600 hover:bg-amber-700',
                    'data-testid': 'confirm-rename-btn'
                },
                    saving ? 'Saving...' : 'Rename'
                )
            )
        )
    );

    return el('div', { className: 'min-h-screen bg-[#FDFCF8]', 'data-testid': 'template-management-page' },
        header,
        mainContent,
        addTemplateModal,
        renameTemplateModal
    );
}

export default TemplateManagement;
